import { isConnected, pinName, pinNode, wireNode, type Analysis } from './analyze';
import { pinsW } from './geometry';
import { fnOf, LIB } from './library';
import { catOf, type PartLookup } from './catalog';
import { t as $t } from './i18n';
import { BAR_NAMES, barPitch, barSupports, barThickness, BEAM_D, SUPPORT_W, BEAM_H, busbarDims, busFed, feet, FOOT, footed, footSpec, layoutDevices, onBar, PHASE_CLEAR, phaseFeet, RAIL_H, strips, terminalYs } from './layout';
import type { PanelLayout, Project, Sym } from './types';

/*
 * 3D panel: the 2D plate and door layouts turned into boxes with depth, for
 * the 3D view and for depth / collision checks. Coordinates in mm: x to the
 * right and y down as on the plate drawing, z from the mounting plate towards
 * the door (the door's inner face is at z = depth).
 */

export type Box3Kind = 'cabinet' | 'plate' | 'rail' | 'duct' | 'device' | 'door' | 'doorDevice' | 'bus' | 'wall' | 'beam' | 'support';

export interface Box3 {
  kind: Box3Kind;
  /** device key and part number for devices */
  key?: string;
  pn?: string;
  label: string;
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
  /** depth of the part is known from the parts database */
  sized?: boolean;
  /** devices: symbol type of the main function ('term' for a terminal strip), so the view can draw its shape */
  type?: string;
  /** terminal strips: number of terminals */
  count?: number;
  /** operator devices and lamps: colour of the cap or lens */
  tint?: Tint;
}

export type Tint = 'green' | 'red' | 'yellow' | 'blue' | 'white' | 'black';

/**
 * Colour of a button cap or lamp lens: from the part's description ("yeşil", "red", "gelb" …), else from what the
 * device does (stop and emergency stop red, start green, fault lamps red, other lamps green).
 */
export function tintOf(type: string, text: string, desc: string): Tint | undefined {
  const t = `${text} ${desc}`.toLocaleLowerCase('tr');
  const named: [Tint, RegExp][] = [
    ['green', /yeşil|green|grün|verde|vert|зелён|зелен|绿|緑/], ['red', /kırmızı|\bred\b|\brot\b|rojo|rouge|vermelh|красн|红|赤/],
    ['yellow', /sarı|yellow|gelb|amarill|jaune|amar|жёлт|желт|黄/], ['blue', /mavi|blue|blau|azul|bleu|син|蓝|青/],
    ['white', /beyaz|white|weiß|weiss|blanc|branc|бел|白/], ['black', /siyah|black|schwarz|negro|noir|preto|чёрн|черн|黑|黒/],
  ];
  const own = named.find(([, re]) => re.test(text.toLocaleLowerCase('tr')));
  if (own) return own[0];
  if (type === 'estop' || type === 'estop_no' || type === 'pb_nc') return 'red';
  if (type === 'pb_no' || type === 'pb_latch') return /stop|dur/.test(t) ? 'red' : 'green';
  if (type === 'lamp' || type === 'lamp_led') return /arıza|ariza|fault|alarm|hata|trip/.test(t) ? 'red' : /uyarı|warn/.test(t) ? 'yellow' : 'green';
  if (type === 'rot01' || type === 'rot012') return 'black';
  return undefined;
}

export interface Cabinet3D {
  /** plate (mounting area) and inner cabinet size */
  w: number;
  h: number;
  depth: number;
  /** index of the door page used, if any */
  doorPage?: number;
  boxes: Box3[];
}

/** Default inner depth of a cabinet when the layout does not say (mm). */
export const DEFAULT_CABINET_DEPTH = 250;
const RAIL_D = 7.5;
/** Door devices: depth behind the door and the part in front of it (push buttons, pilot lights). */
const DOOR_BEHIND = 45;
/** How far door devices stand out in front of the door (the 3D view draws their caps and bezels there). */
export const DOOR_FRONT = 22;
const defaultDepth = (key: string, strip: boolean) => (strip ? 45 : /-(A|PLC)\d/i.test(key) ? 75 : 80);

/** The door page that belongs to a plate page: the next door page after it, else any door page. */
export function doorPageFor(pr: Project, plate: number): number | undefined {
  const isDoor = (i: number) => pr.pages[i]?.kind === 'layout' && !!pr.pages[i].layout?.door && !pr.pages[i].layout?.inside;
  for (let i = plate + 1; i < pr.pages.length && pr.pages[i].kind === 'layout'; i++) if (isDoor(i)) return i;
  const any = pr.pages.findIndex((_, i) => isDoor(i));
  return any >= 0 ? any : undefined;
}

/** 3D model of the cabinet whose mounting plate is on page `pi`. */
export function cabinet3D(pr: Project, A: Analysis, pi: number, parts: PartLookup = catOf): Cabinet3D | null {
  const l = pr.pages[pi]?.layout;
  if (!l || l.door) return null;
  const depth = l.depth || DEFAULT_CABINET_DEPTH;
  const devs = layoutDevices(A, parts);
  // busbars stand this far in front of the plate
  const BAR_Z = 40;
  const boxes: Box3[] = [
    { kind: 'cabinet', label: '', x: 0, y: 0, z: -3, w: l.w, h: l.h, d: depth + 3 },
    { kind: 'plate', label: '', x: 0, y: 0, z: -3, w: l.w, h: l.h, d: 3 },
  ];
  // side walls between the compartments of a floor-standing board
  let sx = 0;
  (l.sections ?? []).slice(0, -1).forEach((w) => {
    sx += w;
    boxes.push({ kind: 'wall', label: '', x: sx - 1, y: 0, z: -3, w: 2, h: l.h, d: depth + 3 });
  });
  for (const r of l.rails) boxes.push({ kind: 'rail', label: '', x: r.x, y: r.y - RAIL_H / 2, z: 0, w: r.len, h: RAIL_H, d: RAIL_D });
  for (const du of l.ducts) boxes.push({ kind: 'duct', label: '', x: du.x, y: du.y, z: 0, w: du.w, h: du.h, d: Math.min(80, Math.max(40, Math.min(du.w, du.h))) });
  for (const b of l.beams ?? []) boxes.push({ kind: 'beam', label: $t('Atkı'), x: b.x, y: b.y, z: 0, w: b.w, h: BEAM_H, d: BEAM_D });
  // phase bars of a floor-standing board: in the middle, one behind the other
  const mid = l.bars?.find((b) => b.mid);
  const phaseZ = (k: number) => BAR_Z + 20 + k * barPitch(mid!.spec);
  const fed = l.bars?.length ? busFed(pr, A) : () => false;
  // symbol type of each device's main function (PLC cards are not main functions: their first channel)
  const types = new Map<string, string>();
  for (const it of A.all) {
    const k = A.devKey.get(it.s.id), role = LIB[it.s.type]?.role;
    if (k && !types.has(k) && role === 'plc') types.set(k, it.s.type);
  }
  const typeOf = (key: string) => { const t = A.mains.get(key)?.[0]?.s.type; return t ? fnOf(t) : types.get(key); };
  for (const it of l.items) {
    const d = devs.get(it.dev);
    if (!d) continue;
    const p = d.pn ? parts(d.pn) : undefined;
    const onRail = l.rails.some((r) => it.x + d.w > r.x && it.x < r.x + r.len && Math.abs(it.y + d.h / 2 - r.y) < RAIL_H / 2 + 1);
    const onBeam = (l.beams ?? []).some((b) => it.x + d.w > b.x && it.x < b.x + b.w && b.y + BEAM_H > it.y && b.y < it.y + d.h);
    const z = onBeam ? BEAM_D : onRail ? RAIL_D : 0;
    boxes.push({
      kind: 'device', key: it.dev, pn: d.pn || undefined, label: d.name, x: it.x, y: it.y, z, w: d.w, h: d.h, d: p?.dp ?? d.dp ?? defaultDepth(it.dev, d.strip), sized: p?.dp != null,
      type: d.strip ? 'term' : typeOf(it.dev), ...(d.strip ? { count: d.count } : {}),
    });
    if (!footed(A, d)) continue;
    // copper on the rear terminals (near the top and bottom edge): the parallel bars of each pole, clamping the
    // terminal, run behind the breaker up and down beyond it; on the busbar side they turn forward to the phase bar
    const fs = footSpec(d), { t: st, w: sw } = busbarDims(fs), ft = barThickness(fs), [ty, by] = terminalYs(d);
    const zc = Math.max(2, z - 20), fedHere = mid && fed(it.dev), under = !!mid && it.y > mid.y;
    for (const f of feet(it.x, d)) {
      const k = f.ph === 'N' ? -1 : +f.ph[1] - 1;
      for (const o of strips(fs)) {
        const x = f.x - ft / 2 + o;
        boxes.push({ kind: 'bus', label: `${f.ph} Cu ${fs}`, x, y: it.y - FOOT, z: zc, w: st, h: ty + FOOT, d: sw });
        boxes.push({ kind: 'bus', label: `${f.ph} Cu ${fs}`, x, y: it.y + by, z: zc, w: st, h: d.h - by + FOOT, d: sw });
        // the busbar side end, forward to the phase bar's depth
        if (fedHere && k >= 0) {
          const pz = phaseZ(k), y = under ? it.y - FOOT : it.y + d.h + FOOT - st;
          const z0 = Math.min(zc, pz), z1 = Math.max(zc + sw, pz + ft);
          boxes.push({ kind: 'bus', label: `${f.ph}`, x, y, z: z0, w: st, h: st, d: z1 - z0 });
        }
      }
    }
  }
  // connection bars: each phase at its own depth like the main busbars, turning back at its start to the terminal
  // copper; neutral connections pass in front of the phase bars (air clearance) and turn back to the N busbar
  if (mid) {
    const front = phaseZ(2) + barThickness(mid.spec) + PHASE_CLEAR;
    for (const k of l.links ?? []) {
      const th = barThickness(k.spec), bw = busbarDims(k.spec).w, bt = busbarDims(k.spec).t;
      const run = (z: number, dz: number, label: string) => {
        for (let n = 0; n < k.pts.length - 1; n++) {
          const [p, q] = [k.pts[n], k.pts[n + 1]], x0 = Math.min(p[0], q[0]), y0 = Math.min(p[1], q[1]), flat = p[1] === q[1];
          boxes.push({ kind: 'bus', label, x: flat ? x0 - th / 2 : x0 - th / 2, y: flat ? y0 - th / 2 : y0 - th / 2, z, w: flat ? Math.abs(q[0] - p[0]) + th : th, h: flat ? th : Math.abs(q[1] - p[1]) + th, d: dz });
        }
      };
      const turn = (pt: [number, number], z0: number, z1: number, label: string) =>
        boxes.push({ kind: 'bus', label, x: pt[0] - th / 2, y: pt[1] - th / 2, z: Math.min(z0, z1), w: th, h: th, d: Math.abs(z1 - z0) });
      if (k.kind === 'N') {
        const lab = `N ${$t('Kuplaj bağlantısı')}`;
        run(front, bw, lab);
        turn(k.pts[0], 20, front, lab);
        turn(k.pts[k.pts.length - 1], BAR_Z, front, lab);
        continue;
      }
      for (const ph of k.ph != null ? [k.ph] : [0, 1, 2]) {
        const lab = `L${ph + 1} ${$t('Kuplaj bağlantısı')}`;
        for (const o of strips(k.spec)) {
          // flat bars standing on edge, as the main busbars: the bar width up and down, the thickness in depth
          for (let n = 0; n < k.pts.length - 1; n++) {
            const [p, q] = [k.pts[n], k.pts[n + 1]], x0 = Math.min(p[0], q[0]), y0 = Math.min(p[1], q[1]), flat = p[1] === q[1];
            boxes.push({ kind: 'bus', label: lab, x: x0 - bw / 2, y: y0 - bw / 2, z: phaseZ(ph) + o, w: flat ? Math.abs(q[0] - p[0]) + bw : bw, h: flat ? bw : Math.abs(q[1] - p[1]) + bw, d: bt });
          }
        }
        if (k.ph != null) turn(k.pts[0], 20, phaseZ(ph) + th, lab);
      }
    }
  }
  // busbar supports from the plate: around the phase packs, and a lower one for N and PE
  if (mid)
    for (const { x, bar } of barSupports(l)) {
      const bw = busbarDims(bar.spec).w, back = phaseZ(2) + barThickness(bar.spec) + 10;
      boxes.push({ kind: 'support', label: $t('Bara mesnedi'), x: x - SUPPORT_W / 2, y: bar.y - bw / 2 - 15, z: 0, w: SUPPORT_W, h: bw + 30, d: back });
      boxes.push({ kind: 'support', label: $t('Bara mesnedi'), x: x - SUPPORT_W / 2, y: bar.mid!.nY - 15, z: 0, w: SUPPORT_W, h: bar.mid!.peY - bar.mid!.nY + 30, d: BAR_Z + busbarDims(bar.n ?? bar.spec).w + 10 });
    }
  for (const bar of l.bars ?? []) {
    if (bar.mid) {
      const th = barThickness(bar.spec), bw = busbarDims(bar.spec).w;
      const bt = busbarDims(bar.spec).t;
      for (let k = 0; k < 3; k++) for (const o of strips(bar.spec)) boxes.push({ kind: 'bus', label: `L${k + 1} Cu ${bar.spec}`, x: bar.x, y: bar.y - bw / 2, z: phaseZ(k) + o, w: bar.w, h: bw, d: bt });
      for (const [nm, y, spec, span] of [['N', bar.mid.nY, bar.n ?? bar.spec, bar.mid.nSpan], ['PE', bar.mid.peY, bar.pe, bar.mid.peSpan]] as const) {
        const t2 = barThickness(spec), [x0, x1] = span ?? [bar.x, bar.x + bar.w];
        boxes.push({ kind: 'bus', label: `${nm} Cu ${spec}`, x: x0, y: y - t2 / 2, z: BAR_Z, w: x1 - x0, h: t2, d: busbarDims(spec).w });
      }
      // from each phase bar, at its own depth, to the feet of the breakers on the busbar circuit
      for (const it of l.items) {
        const d = devs.get(it.dev);
        if (!d || !fed(it.dev) || !onBar(bar, it.x + d.w / 2)) continue;
        const foot = footed(A, d) ? FOOT : 0, down = it.y > bar.y;
        const y0 = down ? bar.y + bw / 2 : it.y + d.h + foot, y1 = down ? it.y - foot : bar.y - bw / 2;
        if (y1 > y0)
          phaseFeet(it.x, d).forEach((x, k) => {
            const fs = footed(A, d) ? footSpec(d) : bar.spec, { t: st, w: sw } = busbarDims(fs), ft = barThickness(fs);
            for (const o of strips(fs)) boxes.push({ kind: 'bus', label: `L${k + 1}`, x: x - ft / 2 + o, y: y0, z: phaseZ(k), w: st, h: y1 - y0, d: Math.min(sw, barPitch(bar.spec) - PHASE_CLEAR) });
          });
      }
      continue;
    }
    BAR_NAMES.forEach((nm, k) => {
      const spec = nm === 'PE' ? bar.pe : bar.spec, th = barThickness(spec);
      boxes.push({ kind: 'bus', label: `${nm} Cu ${spec}`, x: bar.x, y: bar.y + k * barPitch(bar.spec) - th / 2, z: BAR_Z, w: bar.w, h: th, d: busbarDims(spec).w });
    });
    const th = barThickness(bar.spec), bw = busbarDims(bar.spec).w;
    // copper droppers only to the breakers on the busbar circuit (wires marked as busbar); feeders are cabled
    for (const it of l.items) {
      if (!fed(it.dev)) continue;
      const d = devs.get(it.dev);
      if (!d || it.y <= bar.y) continue;
      phaseFeet(it.x, d).forEach((x, k) => {
        const y0 = bar.y + k * barPitch(bar.spec);
        boxes.push({ kind: 'bus', label: `L${k + 1}`, x: x - th / 2, y: y0, z: BAR_Z + bw + 5, w: th, h: it.y - y0, d: Math.min(bw, 40) });
      });
    }
  }
  const di = doorPageFor(pr, pi);
  const door = di != null ? pr.pages[di].layout : undefined;
  if (door) {
    // the door is centred on the plate; door devices reach back into the cabinet
    const ox = (door.w - l.w) / 2, oy = (door.h - l.h) / 2;
    boxes.push({ kind: 'door', label: '', x: -ox, y: -oy, z: depth, w: door.w, h: door.h, d: 2 });
    for (const it of door.items) {
      const d = devs.get(it.dev);
      if (!d) continue;
      const p = d.pn ? parts(d.pn) : undefined;
      const behind = p?.dp ?? DOOR_BEHIND;
      const type = typeOf(it.dev), tint = type ? tintOf(type, p?.d ?? '', d.desc) : undefined;
      boxes.push({
        kind: 'doorDevice', key: it.dev, pn: d.pn || undefined, label: d.name, x: it.x - ox, y: it.y - oy, z: depth - behind, w: d.w, h: d.h, d: behind + 2 + DOOR_FRONT, sized: p?.dp != null,
        ...(type ? { type } : {}), ...(tint ? { tint } : {}),
      });
    }
  }
  return { w: l.w, h: l.h, depth, doorPage: di, boxes };
}

const overlapXY = (a: Box3, b: Box3) => a.x < b.x + b.w - 0.01 && b.x < a.x + a.w - 0.01 && a.y < b.y + b.h - 0.01 && b.y < a.y + a.h - 0.01;

export interface Issue3D {
  sev: 'err' | 'warn';
  msg: string;
  pi: number;
}

/** Depth problems: devices deeper than the cabinet, door devices hitting plate devices when the door is closed. */
export function panel3dIssues(pr: Project, A: Analysis, parts: PartLookup = catOf): Issue3D[] {
  const out: Issue3D[] = [];
  pr.pages.forEach((p, pi) => {
    if (p.kind !== 'layout' || !p.layout || p.layout.door) return;
    const c = cabinet3D(pr, A, pi, parts);
    if (!c) return;
    const plate = c.boxes.filter((b) => b.kind === 'device' || b.kind === 'duct');
    const door = c.boxes.filter((b) => b.kind === 'doorDevice');
    for (const b of plate)
      if (b.kind === 'device' && b.z + b.d > c.depth) out.push({ sev: 'err', msg: $t('{0}: derinliği ({1} mm) pano derinliğine ({2} mm) sığmıyor', b.label, b.z + b.d, c.depth), pi });
    for (const dd of door)
      for (const b of plate)
        if (overlapXY(dd, b) && b.z + b.d > dd.z)
          out.push({ sev: 'err', msg: $t('Kapak kapanınca {0} ile {1} çakışıyor ({2} mm)', dd.label, b.kind === 'duct' ? $t('kablo kanalı') : b.label, Math.round(b.z + b.d - dd.z)), pi });
  });
  return out;
}

/** Cabinet depth of a layout, with the default. */
export const cabinetDepth = (l: PanelLayout) => l.depth || DEFAULT_CABINET_DEPTH;

/* ---------------- wiring inside the cabinet ---------------- */

export interface Wire3D {
  /** wire number and the two ends ("-K1:A1") */
  no: string;
  from: string;
  to: string;
  cs: string;
  col: string;
  /** route through the cable ducts, mm */
  pts: [number, number, number][];
  /** cut length including the reserve at both ends, mm */
  len: number;
}

/** Extra length left at each end of a wire for stripping and connecting (mm). */
export const WIRE_RESERVE = 50;

/**
 * Wires between devices mounted on the plate of page `pi`, routed from each
 * terminal into the nearest cable duct, along the ducts and out to the other
 * terminal. Connections are the same pairs as in the connection list.
 */
export function cabinetWiring(pr: Project, A: Analysis, pi: number, parts: PartLookup = catOf): Wire3D[] {
  const cab = cabinet3D(pr, A, pi, parts);
  const l = pr.pages[pi]?.layout;
  if (!cab || !l) return [];
  const boxOf = new Map(cab.boxes.filter((b) => b.kind === 'device' && b.key).map((b) => [b.key!, b]));
  const ducts = cab.boxes.filter((b) => b.kind === 'duct');
  const hDucts = ducts.filter((d) => d.w > d.h), vDucts = ducts.filter((d) => d.h >= d.w);

  // terminals of a strip in order, to place each one along the strip
  const stripPos = new Map<string, { k: number; n: number }>();
  const strips = new Map<string, Sym[]>();
  for (const it of A.all) if (LIB[it.s.type]?.role === 'term') {
    const key = A.devKey.get(it.s.id) ?? '';
    (strips.get(key) ?? strips.set(key, []).get(key)!).push(it.s);
  }
  for (const list of strips.values()) {
    list.sort((a, b) => (parseInt(a.props.pinNo || '0', 10) || 0) - (parseInt(b.props.pinNo || '0', 10) || 0));
    list.forEach((s, k) => stripPos.set(s.id, { k, n: list.length }));
  }

  /** Terminal point on the device face: top or bottom edge, spread across the width. */
  const pinPoint = (s: Sym, i: number): { p: [number, number, number]; top: boolean } | null => {
    const b = boxOf.get(A.devKey.get(s.id) ?? '');
    if (!b) return null;
    const L = LIB[s.type];
    const ys = L.pins.map((q) => q[1]);
    const top = L.pins[i][1] <= Math.min(...ys) && Math.max(...ys) > Math.min(...ys) ? true : L.pins[i][1] < (Math.min(...ys) + Math.max(...ys)) / 2 || ys.length === 1;
    let f: number;
    const sp = stripPos.get(s.id);
    if (sp) f = (sp.k + 0.5) / sp.n;
    else {
      const xs = L.pins.map((q) => q[0]), x0 = Math.min(...xs), x1 = Math.max(...xs);
      const lab = pinName(A, s, i);
      f = x1 > x0 ? 0.15 + (0.7 * (L.pins[i][0] - x0)) / (x1 - x0) : 0.2 + 0.6 * ((lab.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 7) / 6);
    }
    return { p: [b.x + b.w * f, top ? b.y : b.y + b.h, b.z + b.d - 4], top };
  };

  /** Nearest horizontal duct above (top) or below a point that spans its x; else the nearest one. */
  const ductFor = (x: number, y: number, top: boolean) => {
    const spans = hDucts.filter((d) => x >= d.x - 20 && x <= d.x + d.w + 20);
    const side = spans.filter((d) => (top ? d.y + d.h <= y + 1 : d.y >= y - 1));
    const pick = (list: Box3[]) => list.sort((a, b) => Math.abs(a.y + a.h / 2 - y) - Math.abs(b.y + b.h / 2 - y))[0];
    return pick(side) ?? pick(spans) ?? pick([...hDucts]);
  };

  // connection pairs, as in the connection list
  const nets = new Map<string, { s: Sym; i: number; pi: number }[]>();
  for (const it of A.all) {
    const L = LIB[it.s.type];
    if (!L || ['brk', 'pot', 'text', 'sl', 'box', 'cable'].includes(L.role)) continue;
    L.pins.forEach((_, i) => {
      if (!isConnected(A, it.s, i)) return;
      const r = A.uf.find(pinNode(it.s, i));
      (nets.get(r) ?? nets.set(r, []).get(r)!).push({ s: it.s, i, pi: it.pi });
    });
  }
  const wireOf = new Map<string, { cs: string; col: string }>();
  const busNets = new Set<string>();
  for (const p of pr.pages) for (const w of p.wires) {
    const r = A.uf.find(wireNode(w.id));
    if (w.bus) busNets.add(r);
    if (!wireOf.has(r)) wireOf.set(r, { cs: w.cs, col: w.col });
  }

  const out: Wire3D[] = [];
  for (const [r, list] of nets) {
    // copper busbars are not cut from wire: they are in the busbar system of the 3D view
    if (busNets.has(r)) continue;
    list.sort((a, b) => a.pi - b.pi || a.s.x - b.s.x || pinsW(a.s)[a.i][1] - pinsW(b.s)[b.i][1]);
    for (let k = 0; k < list.length - 1; k++) {
      const a = list[k], b = list[k + 1];
      const pa = pinPoint(a.s, a.i), pb = pinPoint(b.s, b.i);
      if (!pa || !pb) continue;
      const pts: [number, number, number][] = [pa.p];
      const da = ductFor(pa.p[0], pa.p[1], pa.top), db = ductFor(pb.p[0], pb.p[1], pb.top);
      if (da && db) {
        const za = da.z + da.d / 2, zb = db.z + db.d / 2, ya = da.y + da.h / 2, yb = db.y + db.h / 2;
        // straight down/up in front of the device to the duct's edge, then into the duct
        pts.push([pa.p[0], pa.top ? da.y + da.h : da.y, pa.p[2]], [pa.p[0], ya, za]);
        if (da !== db) {
          const mid = (pa.p[0] + pb.p[0]) / 2;
          const v = [...vDucts].sort((m, n) => Math.abs(m.x + m.w / 2 - mid) - Math.abs(n.x + n.w / 2 - mid))[0];
          const vx = v ? v.x + v.w / 2 : pb.p[0], vz = v ? v.z + v.d / 2 : (za + zb) / 2;
          pts.push([vx, ya, vz], [vx, yb, vz]);
        }
        pts.push([pb.p[0], yb, zb], [pb.p[0], pb.top ? db.y + db.h : db.y, pb.p[2]]);
      } else pts.push([pb.p[0], pa.p[1], pa.p[2]]);
      pts.push(pb.p);
      let len = 0;
      for (let q = 1; q < pts.length; q++) len += Math.hypot(pts[q][0] - pts[q - 1][0], pts[q][1] - pts[q - 1][1], pts[q][2] - pts[q - 1][2]);
      const w = wireOf.get(r);
      out.push({
        no: A.netLabel.get(r) ?? '', from: pinName(A, a.s, a.i), to: pinName(A, b.s, b.i),
        cs: w?.cs ?? '', col: w?.col ?? '', pts, len: Math.ceil((len + 2 * WIRE_RESERVE) / 10) * 10,
      });
    }
  }
  return out;
}
